import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

// Bearer-token auth (localStorage). withCredentials MUST stay false:
// the platform proxy returns 'Access-Control-Allow-Origin: *' without
// 'Allow-Credentials', so credentialed requests are blocked inside the
// preview iframe. The Authorization header below is the source of truth.
axios.defaults.withCredentials = false;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mnm_token";

const savedToken = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
if (savedToken) axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;

function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
  }
}

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function formatErr(detail) {
  if (detail == null) return "Algo deu errado. Tenta de novo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=logged out
  const [payProcessing, setPayProcessing] = useState(false);
  const oauthHandled = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setToken(null);
      setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) { console.error("refreshUser failed:", e); }
  }, []);

  // Handle Google OAuth callback + Stripe payment return on mount
  useEffect(() => {
    const hash = window.location.hash || "";
    const path = window.location.pathname || "";
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) localStorage.setItem("mnm_ref", refCode);

    if (hash.includes("session_id=")) {
      if (oauthHandled.current) return;
      oauthHandled.current = true;
      const sid = hash.split("session_id=")[1].split("&")[0];
      (async () => {
        try {
          const ref = localStorage.getItem("mnm_ref");
          const { data } = await axios.post(`${API}/auth/session`, {}, { headers: { "X-Session-ID": sid, ...(ref ? { "X-Referral-Code": ref } : {}) } });
          if (data.session_token) setToken(data.session_token);
          setUser(data);
          localStorage.removeItem("mnm_ref");
          toast.success(`Bem-vinda(o), ${data.name}! 💜`);
        } catch {
          toast.error("Falha no login com Google.");
          await checkAuth();
        }
        window.history.replaceState({}, "", "/");
      })();
      return;
    }

    if (path === "/payment/success") {
      const sid = params.get("session_id");
      setPayProcessing(true);
      (async () => {
        let ok = false;
        for (let i = 0; i < 8; i++) {
          try {
            const { data } = await axios.get(`${API}/payments/status/${sid}`);
            if (data.payment_status === "paid") { ok = true; break; }
          } catch { /* retry */ }
          await new Promise((r) => setTimeout(r, 1500));
        }
        await checkAuth();
        setPayProcessing(false);
        toast[ok ? "success" : "message"](ok ? "Créditos adicionados! 🎉" : "Pagamento em processamento…");
        window.history.replaceState({}, "", "/");
      })();
      return;
    }

    if (path === "/payment/cancel") {
      window.history.replaceState({}, "", "/");
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.session_token) setToken(data.session_token);
    setUser(data);
  };
  const register = async (email, password, name) => {
    const ref = localStorage.getItem("mnm_ref") || undefined;
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, ref });
    if (data.session_token) setToken(data.session_token);
    setUser(data);
    localStorage.removeItem("mnm_ref");
  };
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) { console.error("logout request failed:", e); }
    setToken(null);
    setUser(null);
  };
  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, loginGoogle, refreshUser, payProcessing }}>
      {children}
    </AuthContext.Provider>
  );
}
